import React, { ChangeEvent, FC } from 'react';
import { Checkbox, Grid, Link, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { flatten } from 'lodash';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import moment from 'moment';

import {
  ContentApprovalTypes,
  ContentFragment,
  ContentGroupInput,
  SearchContentSortFields,
  SortDirections,
} from 'api';
import {
  DataTableRowSelection,
  DataTableActions,
  DataTable,
  DataTableColumn,
  TextTransform,
} from 'components';
import { CheckedMode, InfiniteQuery, UseCheckedState } from 'types/app';
import { generateLink } from 'Links';
import { hasVideo, isPhotoOnly, isUrl } from 'features/content/helpers';
import { Body, UrlMetadata } from 'features/activity/components';
import { SortAction, SortParam } from 'components/DataTable';

import { IMAGE_SIZE } from './constants';
import Actions, { UpdateContentMutationOptions } from './Actions';
import PostImage from './PostImage';

interface Props extends Partial<UseCheckedState>, InfiniteQuery<ContentFragment> {
  approval: ContentApprovalTypes;
  count: number;
  updateContentMutationOptions: UpdateContentMutationOptions;
  sortBy: SortParam;
  onSort: SortAction;
}

const PostManagementTable: FC<Props> = ({
  approval,
  sortBy,
  onSort,
  sentinelRef,
  results,
  isExhausted,
  isInitialLoad,
  count,
  updateContentMutationOptions,
  ...rest
}) => {
  const { t } = useTranslation();

  const { setCheckedState, checkedMode, checkedIds } = rest;
  const isAllChecked = checkedMode !== CheckedMode.OFF;
  const showCheckbox = approval === ContentApprovalTypes.Submitted;

  function getType(content: ContentFragment) {
    if (isPhotoOnly(content)) {
      return t('common:image');
    }

    if (hasVideo(content)) {
      return t('common:video');
    }

    if (isUrl(content)) {
      return t('common:link');
    }

    return t('common:Text');
  }

  function getSubmitter(content: ContentFragment) {
    if (content.user) {
      return (
        <Link noWrap component={generateLink(`/profile/${content.user.id}`)}>
          {content.user.full_name}
        </Link>
      );
    }

    return t('components:rssFeeds');
  }

  function getSubmitterText(content: ContentFragment) {
    return content.user ? content.user.full_name : t('components:rssFeeds');
  }

  function getGroupTitles(content: ContentFragment) {
    if (!content.groups.length) {
      return '';
    }

    if (content.groups.length === 1) {
      return (
        <Link component={generateLink(`/group/${content.groups[0].id}`)}>
          {content.groups[0].title}
        </Link>
      );
    }

    return `${content.groups[0]?.title} +${content.groups?.length - 1}`;
  }

  function getGroupTitlesText(content: ContentFragment) {
    switch (content.groups.length) {
      case 0:
        return '';
      case 1:
        return content.groups[0].title;
      default:
        return `${content.groups[0]?.title} +${content.groups?.length - 1}`;
    }
  }

  function handleAllCheckedChange(event: ChangeEvent<HTMLInputElement>) {
    setCheckedState(event.target.checked ? CheckedMode.PAGE : CheckedMode.OFF);
  }

  function handleCheckedChange(checkedId: string, checked: boolean) {
    let nextCheckedIds = [];

    if (checkedMode) {
      setCheckedState(CheckedMode.OFF);
      nextCheckedIds = results.map(item => item.content_id).filter(id => id !== checkedId);
    } else if (checked) {
      nextCheckedIds = checkedIds.slice();
      nextCheckedIds.push(checkedId);
    } else {
      nextCheckedIds = checkedIds.filter(id => id !== checkedId);
    }

    setCheckedState(nextCheckedIds);
  }

  const columns: DataTableColumn<ContentFragment>[] = [
    {
      width: 48,
      headerCell: {
        padding: 'checkbox',
        render: <Checkbox checked={isAllChecked} onChange={handleAllCheckedChange} />,
      },
      bodyCell: {
        padding: 'checkbox',
        render: content => (
          <Checkbox
            name={content.content_id}
            checked={isAllChecked || checkedIds.includes(content.content_id)}
            onChange={event => handleCheckedChange(content.content_id, event.target.checked)}
          />
        ),
      },
    },
    {
      width: IMAGE_SIZE,
      headerCell: {
        padding: 'none',
        render: t('admin:postPreview'),
      },
      bodyCell: {
        padding: 'none',
        render: content => <PostImage content={content} />,
      },
    },
    {
      width: 400,
      headerCell: {
        render: '',
      },
      bodyCell: {
        render: content => {
          return isUrl(content) ? <UrlMetadata content={content} /> : <Body content={content} />;
        },
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:type'),
      },
      bodyCell: {
        render: content => (
          <Typography title={getType(content)} noWrap>
            {getType(content)}
          </Typography>
        ),
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('admin:submitter'),
      },
      bodyCell: {
        render: content => {
          const submitter = getSubmitter(content);
          const submitterText = getSubmitterText(content);

          return (
            <Typography noWrap title={submitterText}>
              {submitter}
            </Typography>
          );
        },
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:role'),
      },
      bodyCell: {
        render: content => (
          <TextTransform transform="capitalize">
            <Typography title={content.submitter_role}>{content.submitter_role}</Typography>
          </TextTransform>
        ),
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('common:group_plural'),
      },
      bodyCell: {
        render: content => {
          const titlesEl = getGroupTitles(content);
          const titleText = getGroupTitlesText(content);

          return (
            <Typography noWrap title={titleText}>
              {titlesEl}
            </Typography>
          );
        },
      },
    },
    {
      width: 200,
      headerCell: {
        sort: {
          field: SearchContentSortFields.CreatedAt,
          direction: SortDirections.Desc,
        },
        render: t('common:date'),
      },
      bodyCell: {
        render: content => {
          const mom = moment.unix(content.publish_at);
          const format = `${mom.format('l')} - ${mom.format('h:mm A')}`;
          return (
            <Typography noWrap title={format}>
              {format}
            </Typography>
          );
        },
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:branded'),
      },
      bodyCell: {
        render: content => {
          return (
            content.is_branded && (
              <Grid container justify="center">
                <Grid item>
                  <CheckRoundedIcon />
                </Grid>
              </Grid>
            )
          );
        },
      },
    },
  ];

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  function getGroupIds(content: ContentFragment) {
    const data = results?.filter(result => result.content_id === content.content_id);
    return flatten(data.map(datum => datum.group_ids));
  }

  const actions: DataTableActions<ContentFragment> = content => {
    const groupIds = getGroupIds(content);

    const contentGroupList = groupIds.map(groupId => ({
      group_id: groupId,
      content_id: content.content_id,
    })) as ContentGroupInput[];

    return (
      <Actions
        variables={{
          bulk_update: {
            approval,
            content_group_list: contentGroupList,
          },
        }}
        count={1}
        contents={results}
        updateContentMutationOptions={updateContentMutationOptions}
      />
    );
  };

  function getColumns() {
    if (!showCheckbox) {
      return columns.slice(1);
    }

    return columns;
  }

  return (
    <DataTable
      data={results}
      columns={getColumns()}
      pagination={pagination}
      actions={actions}
      sortBy={sortBy}
      onSort={onSort}
      rowSelection={
        <DataTableRowSelection
          noun={t('common:post_plural').toLowerCase()}
          count={results.length}
          total={count}
          mode={checkedMode}
          onSelectAll={setCheckedState}
          disableMatchAll
        />
      }
    />
  );
};

export default PostManagementTable;
